// FEAT-17 — the door to the syntax-colouring chunk. LIGHT on purpose: this is
// what `Markdown` imports statically; the engine (highlight.js + 25 grammars)
// arrives through the dynamic import below, only once a prompt actually holds
// a fenced block.
//
// Colour is decoration, never a gate: until the chunk lands — or if it never
// does (offline, a deploy that retired the hashed file mid-test) — a code block
// renders exactly as it did before FEAT-17.
import type { HighlightedCode } from "./highlightEngine";

type Engine = typeof import("./highlightEngine");

let engine: Engine | null = null;
let loading: Promise<boolean> | null = null;

/** A fence opener, as CommonMark reads one (up to 3 spaces of indent). */
const FENCE = /^ {0,3}(```|~~~)/m;

export function hasFencedCode(source: string): boolean {
  return FENCE.test(source);
}

export function highlighterReady(): boolean {
  return engine !== null;
}

/**
 * Loads the engine once. Resolves `true` when it is usable, `false` when the
 * chunk could not be fetched — never rejects. A failed load clears the memo,
 * but that is NOT an in-page retry: the browser keeps a failed `import()` in
 * the page's module map and answers every later one from it without touching
 * the network (Chromium, Firefox and WebKit alike, verified 2026-10-04). So
 * after a failure the blocks stay plain until the next page load, which is
 * the retry — harmless, plain is the pre-FEAT-17 block.
 */
export function loadHighlighter(): Promise<boolean> {
  if (engine) return Promise.resolve(true);
  if (!loading) {
    loading = import("./highlightEngine").then(
      (mod) => {
        engine = mod;
        return true;
      },
      () => {
        loading = null;
        return false;
      },
    );
  }
  return loading;
}

/** null = render the block plain (engine absent, unknown tag, or no tag and no question language). */
export function highlightCode(
  code: string,
  tag: string | undefined,
  fallbackLanguage: string | undefined,
): HighlightedCode | null {
  return engine ? engine.highlightCode(code, tag, fallbackLanguage) : null;
}

/** Test seam: forget the loaded engine so the "not loaded yet" path is reachable. */
export function resetHighlighterForTests(): void {
  engine = null;
  loading = null;
}
